import StepHeader from '../../pages/Organization/public/Journal/SubmissionUpload/components/StepHeader';
import { IStepProps } from '../../pages/Sign-Up/UserDetailsForm';
import { Flex } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useCallback, useEffect, useRef } from 'react';
import {
  scrollToError,
  StepOneForm,
  useUserDetailsData
} from './StepThree/signupHook';
import CustomFormFieldPasswordInput from '../FormField/common/CustomFormFIeldPasswordInput';
import ProfileDetails from './StepThree/ProfileDetails';
import { CustomFormFieldComponent } from '../FormField';
import { OutlineCard } from '@morressier/alembic';

const StepOne = ({
  currentStep,
  totalSteps,
  user,
  customErrorMessage,
  setCustomErrorMessage
}: IStepProps) => {
  const { configs } = useUserDetailsData(user?.organizationId || '');
  const newConfig = {
    FirstName: configs.FirstName,
    MiddleName: configs.MiddleName,
    LastName: configs.LastName,
    PrimaryEmail: configs.PrimaryEmail,
    ORCID: configs.ORCID,
    SecondaryEmail: configs.SecondaryEmail,
    ccEmail: configs.ccEmail,
    Prefix: configs.Prefix,
    Degree: configs.Degree,
    ConfirmPassword: configs.ConfirmPassword,
    Password: configs.Password,
    DateOfBirth: configs.DateOfBirth,
    Gender: configs.Gender,
    PhoneNumber: configs.PhoneNumber,
    LinkedinProfileUrl: configs.LinkedinProfileUrl,
    TermsAndConditionAgreement: configs.TermsAndConditionAgreement,
    copyrightAndLicensingAgreement: configs.copyrightAndLicensingAgreement,
    JobTitle: configs.JobTitle
  };

  const { formState, trigger, getValues, watch, reset } =
    useFormContext<StepOneForm>();

  const { errors, isSubmitted, submitCount } = formState;
  const lastScrolledSubmit = useRef<number | null>(null);

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');
  useEffect(() => {
    if (confirmPassword) {
      trigger('confirmPassword');
    }
  }, [password, confirmPassword, trigger]);

  useEffect(() => {
    if (user) {
      const phoneNumber = user.profileDetails?.phoneNumber || '';
      const countryCallingCode = user.profileDetails?.countryCallingCode || '';
      const fullNumber =
        phoneNumber && countryCallingCode
          ? `+${countryCallingCode}${phoneNumber}`
          : '';
      reset({
        prefix: user?.prefix,
        firstName: user?.firstName,
        middleName: user?.middleName || '',
        lastName: user?.lastName,
        degree: user?.degree,
        jobTitle: user?.jobTitle,
        orcid: user?.orcid,
        secondaryEmail: user?.secondaryEmail,
        profileDetails: {
          phoneNumber: {
            fullNumber: fullNumber || '',
            country: user.profileDetails?.countryCode || '',
            countryCallingCode,
            value: phoneNumber
          },
          linkedinProfileUrl: user?.profileDetails?.linkedinProfileUrl || ''
        },
        password: getValues('password'),
        confirmPassword: getValues('password'),
        isSameAsSecondaryEmail: getValues('isSameAsSecondaryEmail'),
        ccEmail: user?.ccEmail ?? getValues('ccEmail'),
        additionalEmails: user?.additionalEmails?.map(email => ({ email })) ?? [
          { email: '' }
        ]
      });
    }
  }, [user]);

  const scrollToFirstError = useCallback(() => {
    scrollToError(errors);
  }, [errors]);

  useEffect(() => {
    const hasErrors = Object.keys(errors || {}).length > 0;

    if (
      isSubmitted &&
      hasErrors &&
      lastScrolledSubmit.current !== submitCount
    ) {
      lastScrolledSubmit.current = submitCount;

      setTimeout(() => {
        scrollToFirstError();
      }, 100);
    }
  }, [errors, isSubmitted, submitCount, scrollToFirstError]);

  return (
    <>
      <Flex direction={'column'} px={6} id="PersonalDetails">
        <StepHeader
          currentStep={currentStep}
          totalSteps={totalSteps}
          heading="Account Details"
          description="Tell us who you are and set a password for this account."
        />
        <OutlineCard width="100%" padding={4}>
          <Flex direction={'column'} gap={4}>
            <ProfileDetails
              configs={newConfig}
              setCustomErrorMessage={setCustomErrorMessage}
              customErrorMessage={customErrorMessage}
              primaryEmail={user?.email || ''}
              showAdditionalEmails
            />

            <Flex id="PasswordDetails" direction={'column'} gap={4}>
              {newConfig?.Password && (
                <CustomFormFieldPasswordInput config={newConfig?.Password} />
              )}

              {newConfig?.ConfirmPassword && (
                <CustomFormFieldPasswordInput
                  config={newConfig?.ConfirmPassword}
                  dependentField={'password'}
                />
              )}
            </Flex>
            <Flex flexDirection="column" gap={2} width={'fit-content'}>
              {configs?.TermsAndConditionAgreement && (
                <CustomFormFieldComponent
                  config={configs?.TermsAndConditionAgreement}
                />
              )}
            </Flex>
            <Flex flexDirection="column" gap={2} mb={3} width={'fit-content'}>
              {configs?.copyrightAndLicensingAgreement && (
                <CustomFormFieldComponent
                  config={configs?.copyrightAndLicensingAgreement}
                />
              )}
            </Flex>
          </Flex>
        </OutlineCard>
      </Flex>
    </>
  );
};

export default StepOne;
